<template>
  <div class="communication-panel space-y-4">
    <!-- Communications du jour -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-bold text-gray-800 flex items-center gap-2">📢 Communications du jour</h3>
        <div class="flex items-center gap-2">
          <button @click="loadCommunications" class="text-gray-400 text-sm" title="Actualiser">🔄</button>
          <button v-if="!readonly" @click="showAddMessage = true" class="text-blue-600 text-sm font-medium">
            + Nouveau
          </button>
        </div>
      </div>

      <div class="space-y-2">
        <p v-if="messagesError" class="text-sm text-red-600 text-center py-2">{{ messagesError }}</p>
        <div v-else-if="messagesLoading" class="text-sm text-gray-500 text-center py-2">Chargement…</div>
        <div v-for="msg in messages" :key="msg.id" :class="getMessageClass(msg.type)" class="rounded-lg p-3 border-l-4">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <span class="font-semibold text-sm">{{ msg.title }}</span>
                <span class="text-xs bg-white px-2 py-1 rounded">{{ msg.source }}</span>
              </div>
              <p class="text-sm text-gray-700">{{ msg.content }}</p>
              <p class="text-xs text-gray-500 mt-1">{{ msg.time }}</p>
            </div>
            <button
              v-if="msg.actionRequired"
              @click="acknowledgeMessage(msg.id)"
              class="ml-3 text-xs bg-white px-3 py-1 rounded-full font-medium hover:bg-gray-100"
            >
              ✓ Vu
            </button>
            <button
              v-if="!readonly"
              @click="deleteMessage(msg)"
              :disabled="deletingMessageId === msg.id"
              class="ml-2 text-xs text-red-400 hover:text-red-600"
              title="Supprimer"
            >
              {{ deletingMessageId === msg.id ? "…" : "✕" }}
            </button>
          </div>
        </div>
        <p
          v-if="messages.length === 0 && !messagesLoading && !messagesError"
          class="text-sm text-gray-500 text-center py-2"
        >
          Aucun message aujourd'hui.
        </p>
      </div>
    </div>

    <!-- Absences déclarées -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-bold text-gray-800">👥 Absences déclarées</h3>
        <button
          v-if="!readonly"
          @click="showAbsenceForm = true"
          class="text-sm bg-blue-500 text-white px-3 py-1 rounded-lg font-medium hover:bg-blue-600"
        >
          Déclarer
        </button>
        <span v-else class="text-xs text-gray-400 italic">Lecture seule</span>
      </div>

      <div v-if="absencesLoading" class="text-center text-sm text-gray-500 py-2">Chargement…</div>

      <div v-else>
        <div class="grid grid-cols-2 gap-3 mb-3">
          <div class="bg-red-50 rounded-lg p-3 text-center">
            <div class="text-2xl font-bold text-red-600">{{ totalAbsences }}</div>
            <div class="text-xs text-gray-600">Absences aujourd'hui</div>
          </div>
          <div class="bg-green-50 rounded-lg p-3 text-center">
            <div class="text-2xl font-bold text-green-600">{{ adjustedBarquettes }}</div>
            <div class="text-xs text-gray-600">Barquettes ajustées</div>
          </div>
        </div>

        <!-- Spécificités totales (depuis AbsenceLevel) -->
        <div
          v-if="
            absenceLevelsSummary.noPork ||
              absenceLevelsSummary.gluten ||
              absenceLevelsSummary.lactose ||
              absenceLevelsSummary.vegetarian
          "
          class="space-y-1 mt-2"
        >
          <p class="text-xs font-semibold text-gray-600">Spécificités absentes :</p>
          <div v-if="absenceLevelsSummary.noPork" class="flex justify-between text-sm">
            <span class="text-gray-700">Sans porc</span>
            <span class="font-semibold text-red-600">-{{ absenceLevelsSummary.noPork }}</span>
          </div>
          <div v-if="absenceLevelsSummary.gluten" class="flex justify-between text-sm">
            <span class="text-gray-700">Allergies gluten</span>
            <span class="font-semibold text-red-600">-{{ absenceLevelsSummary.gluten }}</span>
          </div>
          <div v-if="absenceLevelsSummary.lactose" class="flex justify-between text-sm">
            <span class="text-gray-700">Allergies lactose</span>
            <span class="font-semibold text-red-600">-{{ absenceLevelsSummary.lactose }}</span>
          </div>
          <div v-if="absenceLevelsSummary.vegetarian" class="flex justify-between text-sm">
            <span class="text-gray-700">Végétariens</span>
            <span class="font-semibold text-red-600">-{{ absenceLevelsSummary.vegetarian }}</span>
          </div>
        </div>

        <!-- Détail par niveau -->
        <div v-if="aggregatedAbsenceLevels.length > 0" class="mt-3 space-y-1">
          <p class="text-xs font-semibold text-gray-600">Par niveau :</p>
          <div v-for="lvl in aggregatedAbsenceLevels" :key="lvl.level" class="flex justify-between text-sm">
            <span class="text-gray-700">{{ levelLabel(lvl.level) }}</span>
            <span class="font-semibold text-red-600">-{{ lvl.nb_standard_meals }} personnes</span>
          </div>
        </div>

        <p v-else-if="!absencesLoading && totalAbsences === 0" class="text-sm text-gray-500 text-center py-2">
          Aucune absence déclarée pour aujourd'hui.
        </p>
      </div>
    </div>

    <!-- Spécificités alimentaires -->
    <div class="bg-white rounded-xl p-4 shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-bold text-gray-800">🍽️ Spécificités du jour</h3>
      </div>
      <p class="text-xs text-gray-500">Ces données sont calculées depuis les effectifs du service.</p>
    </div>

    <!-- Historique -->
    <button
      @click="showHistory = true"
      class="w-full bg-gray-100 hover:bg-gray-200 rounded-lg p-3 text-sm font-medium text-gray-700 transition"
    >
      📋 Voir l'historique des communications
    </button>

    <!-- Modal: nouveau message -->
    <div v-if="showAddMessage" class="modal-overlay" @click="showAddMessage = false">
      <div class="modal-content" @click.stop>
        <h3 class="font-bold text-lg mb-4">Nouveau message</h3>
        <div class="space-y-3">
          <div>
            <label class="block text-sm font-semibold mb-1">Type</label>
            <select v-model="newMessage.type" class="w-full p-2 border-2 rounded-lg">
              <option value="absence">Absence</option>
              <option value="info">Information</option>
              <option value="alert">Alerte</option>
              <option value="incident">Incident</option>
              <option value="dietary">Régime alimentaire</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Titre</label>
            <input
              v-model="newMessage.title"
              class="w-full p-2 border-2 rounded-lg"
              placeholder="Ex: Sortie scolaire CM2"
            />
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Message</label>
            <textarea
              v-model="newMessage.content"
              class="w-full p-2 border-2 rounded-lg"
              rows="3"
              placeholder="Détails..."
            ></textarea>
          </div>
          <div class="flex gap-2">
            <button @click="addMessage" class="flex-1 bg-blue-500 text-white py-2 rounded-lg font-semibold">
              Envoyer
            </button>
            <button @click="showAddMessage = false" class="flex-1 bg-gray-200 py-2 rounded-lg font-semibold">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: déclaration d'absence -->
    <div v-if="showAbsenceForm && !readonly" class="modal-overlay" @click="showAbsenceForm = false">
      <div class="modal-content" @click.stop>
        <h3 class="font-bold text-lg mb-4">Déclarer des absences</h3>
        <div class="space-y-3">
          <div>
            <label class="block text-sm font-semibold mb-1">Niveau</label>
            <select v-model="newAbsence.level" class="w-full p-2 border-2 rounded-lg">
              <option v-for="opt in levelOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Absents (standard)</label>
            <input
              v-model.number="newAbsence.nb_standard_meals"
              type="number"
              min="0"
              class="w-full p-2 border-2 rounded-lg"
            />
          </div>
          <p class="text-xs font-semibold text-gray-600">Dont spécificités absentes :</p>
          <div>
            <label class="block text-sm font-semibold mb-1">Sans porc</label>
            <input
              v-model.number="newAbsence.nb_no_pork_meals"
              type="number"
              min="0"
              class="w-full p-2 border-2 rounded-lg"
            />
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Allergies gluten</label>
            <input
              v-model.number="newAbsence.nb_gluten_free_meals"
              type="number"
              min="0"
              class="w-full p-2 border-2 rounded-lg"
            />
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Allergies lactose</label>
            <input
              v-model.number="newAbsence.nb_lactose_free_meals"
              type="number"
              min="0"
              class="w-full p-2 border-2 rounded-lg"
            />
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">Végétariens</label>
            <input
              v-model.number="newAbsence.nb_vegetarian_meals"
              type="number"
              min="0"
              class="w-full p-2 border-2 rounded-lg"
            />
          </div>
          <p v-if="absenceSaveError" class="text-sm text-red-600">{{ absenceSaveError }}</p>
          <div class="flex gap-2">
            <button
              @click="addAbsence"
              :disabled="absenceSaving"
              class="flex-1 bg-blue-500 text-white py-2 rounded-lg font-semibold"
            >
              {{ absenceSaving ? "Enregistrement…" : "Valider" }}
            </button>
            <button @click="showAbsenceForm = false" class="flex-1 bg-gray-200 py-2 rounded-lg font-semibold">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const LEVEL_LABELS = {
  MATERNELLE: "Maternelle",
  ELEMENTAIRE: "Élémentaire",
  COLLEGE: "Collège",
  LYCEE: "Lycée",
  ENSEIGNANT: "Enseignant",
  PS: "PS (Petite Section)",
  MS: "MS (Moyenne Section)",
  GS: "GS (Grande Section)",
  CP: "CP",
  CE1: "CE1",
  CE2: "CE2",
  CM1: "CM1",
  CM2: "CM2",
  "ANIM-MAT": "Animateurs maternelle",
  "ANIM-ELE": "Animateurs élémentaire",
  SIXIEME: "6ème",
  CINQUIEME: "5ème",
  QUATRIEME: "4ème",
  TROISIEME: "3ème",
  SECONDE: "2nde",
  PREMIERE: "1ère",
  TERMINALE: "Terminale",
}

const LEVELS_BY_VARIANT = {
  SIMPLE: ["MATERNELLE", "ELEMENTAIRE", "COLLEGE", "LYCEE", "ENSEIGNANT"],
  PRIMAIRE: ["PS", "MS", "GS", "CP", "CE1", "CE2", "CM1", "CM2", "ANIM-MAT", "ANIM-ELE", "ENSEIGNANT"],
  SECONDAIRE: ["SIXIEME", "CINQUIEME", "QUATRIEME", "TROISIEME", "SECONDE", "PREMIERE", "TERMINALE", "ENSEIGNANT"],
}

export default {
  name: "CommunicationPanel2",

  props: {
    canteenId: { type: Number, default: null },
    date: { type: String, default: null },
    variant: { type: String, default: "SIMPLE" },
    serviceId: { type: Number, default: null },
    readonly: { type: Boolean, default: false },
  },

  data() {
    return {
      messages: [],
      messagesLoading: false,
      messagesError: null,
      absencesLoading: false,
      absenceRecord: null,
      absenceLevels: [],
      serviceLevels: [],
      absenceSaving: false,
      absenceSaveError: null,
      showAddMessage: false,
      showAbsenceForm: false,
      showHistory: false,
      deletingMessageId: null,
      newMessage: { type: "info", title: "", content: "" },
      newAbsence: {
        level: "MATERNELLE",
        nb_standard_meals: 0,
        nb_no_pork_meals: 0,
        nb_gluten_free_meals: 0,
        nb_lactose_free_meals: 0,
        nb_vegetarian_meals: 0,
      },
    }
  },

  computed: {
    levelOptions() {
      // Priorité aux niveaux réellement configurés sur le service courant : c'est la seule liste
      // qui garantit l'existence d'un barème nb_persons_per_tray pour le calcul des barquettes.
      // À défaut (service non chargé), on retombe sur la liste théorique de la variante.
      if (this.serviceLevels.length > 0) {
        return this.serviceLevels.map((s) => ({ value: s.level, label: LEVEL_LABELS[s.level] || s.level }))
      }
      const levels = LEVELS_BY_VARIANT[this.variant] || LEVELS_BY_VARIANT.SIMPLE
      return levels.map((v) => ({ value: v, label: LEVEL_LABELS[v] || v }))
    },
    totalAbsences() {
      return this.absenceLevels.reduce((s, l) => s + (l.nb_standard_meals || 0), 0)
    },
    aggregatedAbsenceLevels() {
      // Regroupe les lignes par niveau et cumule les effectifs absents.
      // Robuste aux données déjà dupliquées en base (ex. 10 CE1 + 5 CE1 → 15 CE1).
      const byLevel = {}
      for (const l of this.absenceLevels) {
        if (!byLevel[l.level]) {
          byLevel[l.level] = { level: l.level, nb_standard_meals: 0 }
        }
        byLevel[l.level].nb_standard_meals += l.nb_standard_meals || 0
      }
      return Object.values(byLevel)
    },
    absenceLevelsSummary() {
      return {
        noPork: this.absenceLevels.reduce((s, l) => s + (l.nb_no_pork_meals || 0), 0),
        gluten: this.absenceLevels.reduce((s, l) => s + (l.nb_gluten_free_meals || 0), 0),
        lactose: this.absenceLevels.reduce((s, l) => s + (l.nb_lactose_free_meals || 0), 0),
        vegetarian: this.absenceLevels.reduce((s, l) => s + (l.nb_vegetarian_meals || 0), 0),
      }
    },
    adjustedBarquettes() {
      if (this.serviceLevels.length === 0) return this.totalAbsences
      // Barquettes à mettre de côté = somme, par niveau, de (absents / personnes par barquette),
      // chaque niveau étant arrondi à 2 décimales (ex. 23/8=2.88 + 17/10=1.70 + 20/12=1.67 = 6.25).
      const total = this.absenceLevels.reduce((sum, l) => {
        const sl = this.serviceLevels.find((s) => s.level === l.level)
        const perTray = sl?.nb_persons_per_tray || 1
        const trays = (l.nb_standard_meals || 0) / perTray
        return sum + Math.round(trays * 100) / 100
      }, 0)
      // Arrondi final à 2 décimales pour neutraliser les imprécisions de calcul flottant.
      return Math.round(total * 100) / 100
    },
  },

  watch: {
    canteenId() {
      this.loadAbsences()
      this.loadCommunications()
    },
    date() {
      this.loadAbsences()
      this.loadCommunications()
    },
    serviceId(val) {
      if (val) this.loadServiceLevels()
    },
    serviceLevels(val) {
      // Aligner le niveau sélectionné sur les niveaux du service (évite un niveau orphelin → perTray=1).
      if (val.length > 0 && !val.some((s) => s.level === this.newAbsence.level)) {
        this.newAbsence.level = val[0].level
      }
    },
    variant(val) {
      const levels = LEVELS_BY_VARIANT[val] || LEVELS_BY_VARIANT.SIMPLE
      this.newAbsence.level = levels[0]
    },
  },

  mounted() {
    if (this.canteenId && this.date) {
      this.loadAbsences()
      this.loadCommunications()
    }
    if (this.serviceId) this.loadServiceLevels()
  },

  methods: {
    levelLabel(level) {
      return LEVEL_LABELS[level] || level
    },

    defaultLevel() {
      // Premier niveau du service si disponible, sinon premier niveau théorique de la variante.
      if (this.serviceLevels.length > 0) return this.serviceLevels[0].level
      const levels = LEVELS_BY_VARIANT[this.variant] || LEVELS_BY_VARIANT.SIMPLE
      return levels[0]
    },

    normalizeServiceLevel(l) {
      return {
        level: l.level,
        nb_persons_per_tray: l.nb_persons_per_tray ?? l.nbPersonsPerTray ?? 1,
      }
    },

    normalizeAbsenceLevel(l) {
      return {
        id: l.id ?? null,
        absence: l.absence,
        level: l.level,
        nb_standard_meals: l.nb_standard_meals ?? l.nbStandardMeals ?? 0,
        nb_no_pork_meals: l.nb_no_pork_meals ?? l.nbNoPorkMeals ?? 0,
        nb_gluten_free_meals: l.nb_gluten_free_meals ?? l.nbGlutenFreeMeals ?? 0,
        nb_lactose_free_meals: l.nb_lactose_free_meals ?? l.nbLactoseFreeMeals ?? 0,
        nb_vegetarian_meals: l.nb_vegetarian_meals ?? l.nbVegetarianMeals ?? 0,
      }
    },

    async loadServiceLevels() {
      if (!this.serviceId) return
      const levels = await this.$store.dispatch("fetchServiceLevels", this.serviceId)
      this.serviceLevels = Array.isArray(levels) ? levels.map(this.normalizeServiceLevel) : []
    },

    async loadAbsences() {
      if (!this.canteenId || !this.date) return
      this.absencesLoading = true
      try {
        const absences = await this.$store.dispatch("fetchAbsences", {
          canteenId: this.canteenId,
          date: this.date,
        })
        const dayAbsences = Array.isArray(absences) ? absences : []
        // Cible d'ajout : 1re fiche du jour (le back garantit l'unicité par cantine/date).
        this.absenceRecord = dayAbsences.length > 0 ? dayAbsences[0] : null
        if (dayAbsences.length > 0) {
          // Affichage : agrège les niveaux de toutes les fiches du jour (robuste au legacy).
          const levelLists = await Promise.all(dayAbsences.map((a) => this.$store.dispatch("fetchAbsenceLevels", a.id)))
          this.absenceLevels = levelLists.flatMap((l) => (Array.isArray(l) ? l : [])).map(this.normalizeAbsenceLevel)
        } else {
          this.absenceLevels = []
        }
      } finally {
        this.absencesLoading = false
      }
    },

    getMessageClass(type) {
      const classes = {
        absence: "bg-red-50 border-red-500",
        info: "bg-blue-50 border-blue-500",
        alert: "bg-orange-50 border-orange-500",
        incident: "bg-orange-50 border-orange-500",
        dietary: "bg-green-50 border-green-500",
      }
      return classes[type] || "bg-gray-50 border-gray-500"
    },

    acknowledgeMessage(id) {
      const msg = this.messages.find((m) => m.id === id)
      if (msg) msg.actionRequired = false
    },

    async loadCommunications() {
      if (!this.canteenId || !this.date) return
      this.messagesLoading = true
      this.messagesError = null
      try {
        const resp = await fetch(`/api/v1/canteen/${this.canteenId}/communications/?date=${this.date}`, {
          credentials: "same-origin",
        })
        if (resp.ok) {
          const data = await resp.json()
          const items = Array.isArray(data) ? data : data.results || []
          this.messages = items.map((c) => ({
            id: c.id,
            type: (c.category || "info").toLowerCase(),
            title: c.title,
            content: c.body,
            source: c.authorName || "—",
            time: new Date(c.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
            actionRequired: c.category === "ALERT" || c.category === "INCIDENT",
          }))
        } else if (resp.status === 403) {
          this.messagesError = "Accès refusé : vous n'êtes pas membre de cette cantine."
        } else {
          this.messagesError = `Erreur ${resp.status} lors du chargement des communications.`
        }
      } catch {
        this.messagesError = "Impossible de charger les communications."
      } finally {
        this.messagesLoading = false
      }
    },

    async addMessage() {
      if (!this.newMessage.title || !this.newMessage.content) return
      if (!this.canteenId || !this.date) return
      const categoryMap = { info: "INFO", alert: "ALERT", absence: "ABSENCE", incident: "INCIDENT", dietary: "DIETARY" }
      const payload = {
        meal_date: this.date,
        category: categoryMap[this.newMessage.type] || "INFO",
        title: this.newMessage.title,
        body: this.newMessage.content,
      }
      try {
        const resp = await fetch(`/api/v1/canteen/${this.canteenId}/communications/`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCsrfToken() },
          body: JSON.stringify(payload),
        })
        if (resp.ok) {
          this.newMessage = { type: "info", title: "", content: "" }
          this.showAddMessage = false
          this.messagesError = null
          await this.loadCommunications()
        }
      } catch (err) {
        console.error("Erreur création communication:", err)
      }
    },

    getCsrfToken() {
      const match = document.cookie.match(/csrftoken=([^;]+)/)
      return match ? match[1] : ""
    },

    async deleteMessage(msg) {
      if (!confirm(`Supprimer le message « ${msg.title} » ?`)) return
      // Message d'absence injecté localement (non persisté en communication) : retrait direct, sans appel API.
      if (msg.local) {
        this.messages = this.messages.filter((m) => m.id !== msg.id)
        return
      }
      this.deletingMessageId = msg.id
      try {
        const resp = await fetch(`/api/v1/canteen/${this.canteenId}/communications/${msg.id}/`, {
          method: "DELETE",
          credentials: "same-origin",
          headers: { "X-CSRFToken": this.getCsrfToken() },
        })
        if (resp.ok || resp.status === 204) {
          this.messages = this.messages.filter((m) => m.id !== msg.id)
        } else if (resp.status === 403) {
          alert("Vous ne pouvez supprimer que vos propres communications.")
        } else {
          alert(`Erreur ${resp.status} lors de la suppression.`)
        }
      } catch (e) {
        alert("Erreur réseau : " + e.message)
      } finally {
        this.deletingMessageId = null
      }
    },

    async addAbsence() {
      if (this.newAbsence.nb_standard_meals < 1) return
      if (!this.canteenId || !this.date) return

      this.absenceSaving = true
      this.absenceSaveError = null
      try {
        if (!this.absenceRecord) {
          this.absenceRecord = await this.$store.dispatch("createAbsence", {
            canteen: this.canteenId,
            date: this.date,
            manager: this.$store.state.loggedUser?.id || null,
          })
        }

        const levelPayload = {
          absence: this.absenceRecord.id,
          level: this.newAbsence.level,
          nb_standard_meals: this.newAbsence.nb_standard_meals,
          nb_no_pork_meals: this.newAbsence.nb_no_pork_meals,
          nb_gluten_free_meals: this.newAbsence.nb_gluten_free_meals,
          nb_lactose_free_meals: this.newAbsence.nb_lactose_free_meals,
          nb_vegetarian_meals: this.newAbsence.nb_vegetarian_meals,
        }
        const created = await this.$store.dispatch("createAbsenceLevel", levelPayload)
        const normalized = this.normalizeAbsenceLevel(created)
        // Le back agrège sur la ligne (absence, niveau) existante : on remplace
        // la ligne locale de même id le cas échéant, plutôt que d'en empiler une 2e.
        const idx = this.absenceLevels.findIndex((l) => l.id === normalized.id)
        if (idx !== -1) {
          this.$set(this.absenceLevels, idx, normalized)
        } else {
          this.absenceLevels.push(normalized)
        }

        this.messages.unshift({
          id: `local-${this.absenceRecord.id}-${this.newAbsence.level}`,
          local: true,
          type: "absence",
          title: `Absences ${this.levelLabel(this.newAbsence.level)}`,
          content: `${this.newAbsence.nb_standard_meals} personnes absentes`,
          source: "Vous",
          time: new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
          actionRequired: false,
        })

        this.newAbsence = {
          level: this.defaultLevel(),
          nb_standard_meals: 0,
          nb_no_pork_meals: 0,
          nb_gluten_free_meals: 0,
          nb_lactose_free_meals: 0,
          nb_vegetarian_meals: 0,
        }
        this.showAbsenceForm = false
      } catch {
        this.absenceSaveError = "Une erreur est survenue lors de la déclaration."
      } finally {
        this.absenceSaving = false
      }
    },
  },
}
</script>

<style scoped>
.space-y-4 > * + * {
  margin-top: 1rem;
}
.space-y-3 > * + * {
  margin-top: 0.75rem;
}
.space-y-2 > * + * {
  margin-top: 0.5rem;
}
.space-y-1 > * + * {
  margin-top: 0.25rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-1 {
  flex: 1 1 0%;
}
.items-start {
  align-items: flex-start;
}
.items-center {
  align-items: center;
}
.justify-between {
  justify-content: space-between;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.rounded-full {
  border-radius: 9999px;
}
.rounded {
  border-radius: 0.25rem;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.border-l-4 {
  border-left-width: 4px;
}
.border-2 {
  border-width: 2px;
}
.border-red-500 {
  border-color: #ef4444;
}
.border-blue-500 {
  border-color: #2563eb;
}
.border-green-500 {
  border-color: #22c55e;
}
.border-orange-500 {
  border-color: #f97316;
}
.border-gray-500 {
  border-color: #6b7280;
}
.bg-white {
  background-color: #ffffff;
}
.bg-red-50 {
  background-color: #fef2f2;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.bg-green-50 {
  background-color: #f0fdf4;
}
.bg-orange-50 {
  background-color: #fff7ed;
}
.bg-gray-50 {
  background-color: #f9fafb;
}
.bg-gray-100 {
  background-color: #f3f4f6;
}
.bg-gray-200 {
  background-color: #e5e7eb;
}
.hover\:bg-gray-100:hover {
  background-color: #f3f4f6;
}
.hover\:bg-gray-200:hover {
  background-color: #e5e7eb;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.bg-blue-600 {
  background-color: #2563eb;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.text-white {
  color: #ffffff;
}
.text-blue-600 {
  color: #2563eb;
}
.text-red-600 {
  color: #dc2626;
}
.text-green-600 {
  color: #16a34a;
}
.text-orange-600 {
  color: #ea580c;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-700 {
  color: #374151;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.font-medium {
  font-weight: 500;
}
.text-lg {
  font-size: 1.125rem;
}
.text-sm {
  font-size: 0.875rem;
}
.text-xs {
  font-size: 0.75rem;
}
.text-2xl {
  font-size: 1.5rem;
}
.text-center {
  text-align: center;
}
.block {
  display: block;
}
.p-2 {
  padding: 0.5rem;
}
.p-3 {
  padding: 0.75rem;
}
.p-4 {
  padding: 1rem;
}
.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.mb-1 {
  margin-bottom: 0.25rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.mb-4 {
  margin-bottom: 1rem;
}
.mt-2 {
  margin-top: 0.5rem;
}
.mt-3 {
  margin-top: 0.75rem;
}
.ml-3 {
  margin-left: 0.75rem;
}
.w-full {
  width: 100%;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

button {
  cursor: pointer;
  border: none;
  background: none;
}
button:hover {
  opacity: 0.9;
}
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
input,
select,
textarea {
  font-family: inherit;
  border-color: #e5e7eb;
}
input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 1rem;
}
.modal-content {
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  max-width: 90%;
  width: 400px;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  max-height: 90vh;
  overflow-y: auto;
}
</style>
